import { VideoJsPlayer } from 'video.js';

import { ErrorCode, PlayerError } from '../player-errors';

const isPlayerError = (err: unknown): err is PlayerError => {
  return err instanceof PlayerError;
};
const isPlayNotAllowedError = (err: unknown): err is PlayerError => {
  return isPlayerError(err) && err.code === ErrorCode.PLAYER_ERR_PLAY_NOT_ALLOWED;
};
const getPlayerErrorMessage = (err: unknown): string => {
  return isPlayerError(err) ? err.message : `${err}`;
};

export async function tryToPlay(player: VideoJsPlayer) {
  try {
    await player.play();
  } catch (err) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      throw new PlayerError(ErrorCode.PLAYER_ERR_PLAY_NOT_ALLOWED, err.message);
    } else {
      throw new PlayerError(ErrorCode.PLAYER_ERR_PLAY_NOT_POSSIBLE, `${err instanceof Error ? err.message : err}`);
    }
  }
}

export async function tryToAutoplayMuted(player: VideoJsPlayer) {
  const prevMutedState = player.muted();
  player.muted(true);

  try {
    await tryToAutoplay(player);
  } catch (err) {
    player.muted(prevMutedState);

    throw err;
  }
}

export async function tryToAutoplay(player: VideoJsPlayer) {
  // Firefox supports autoplay policy check
  const autoplayPolicy = getAutoplayPolicy();

  switch (autoplayPolicy) {
    case 'allowed':
      try {
        await tryToPlay(player);
      } catch (err) {
        if (isPlayNotAllowedError(err)) {
          throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_ALLOWED, err.message);
        } else {
          throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_POSSIBLE, getPlayerErrorMessage(err));
        }
      }
      break;
    case 'allowed-muted':
      try {
        await tryToPlayMuted(player);
      } catch (err) {
        if (isPlayNotAllowedError(err)) {
          throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_ALLOWED, err.message);
        } else {
          throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_POSSIBLE, getPlayerErrorMessage(err));
        }
      }
      break;
    case 'disallowed':
      throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_ALLOWED, "Browser policy doesn't allow autoplay.");
    default:
      await tryToAutoplayFallback(player);
      break;
  }
}

async function tryToPlayMuted(player: VideoJsPlayer) {
  const prevMutedState = player.muted();
  player.muted(true);

  try {
    await tryToPlay(player);
  } catch (err) {
    player.muted(prevMutedState);

    throw err;
  }
}

/**
 * Tries to autoplay the video without checking the autoplay policy.
 *
 * This is a fallback for browsers that do not support the autoplay policy check.
 */
async function tryToAutoplayFallback(player: VideoJsPlayer) {
  try {
    await tryToPlay(player);
  } catch (err) {
    if (isPlayNotAllowedError(err)) {
      try {
        await tryToPlayMuted(player);
      } catch (err) {
        if (isPlayNotAllowedError(err)) {
          throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_ALLOWED, err.message);
        } else {
          throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_POSSIBLE, getPlayerErrorMessage(err));
        }
      }
    } else {
      throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_POSSIBLE, getPlayerErrorMessage(err));
    }
  }
}

type AutoplayPolicy = 'allowed' | 'allowed-muted' | 'disallowed';

function getAutoplayPolicy(): AutoplayPolicy | undefined {
  // only firefox supports autoplay policy check
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/getAutoplayPolicy

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const navigator = window.navigator as any;
  return navigator.getAutoplayPolicy ? navigator.getAutoplayPolicy('mediaelement') : undefined;
}
